/**
 * The vault's rules as the screen states them — pure, so they can be tested
 * without a wallet or a chain.
 *
 * Two questions live here. Whether a wallet's licences are all known to us
 * (the vault pays everything at once, so a claim with a partial list is
 * refused on chain — better said before the button than after), and what a
 * refusal from the contract means in one sentence.
 */

export interface Completeness {
  /** iNODE licences the registry knows, with a token id. */
  known: number
  /** iNODE licences the registry knows, with or without a token id. */
  registered: number
  /** What the collection says the wallet holds right now. */
  onChain: number
}

export type CompletenessVerdict =
  | { ok: true }
  | { ok: false; reason: 'unmapped'; known: number; total: number }
  | { ok: false; reason: 'chain-differs'; known: number; total: number }

/**
 * Can this wallet claim with the ids we know?
 *
 * The chain is the authority on how many licences the wallet holds; the
 * registry is where the ids come from. If the registry lacks an id, or the
 * chain counts differently from the registry, the claim would be refused with
 * NotAllLicences — so the answer is "not yet", with the numbers.
 */
export function completeness(c: Completeness): CompletenessVerdict {
  if (c.known < c.registered) {
    return { ok: false, reason: 'unmapped', known: c.known, total: c.registered }
  }
  if (c.known !== c.onChain) {
    return { ok: false, reason: 'chain-differs', known: c.known, total: c.onChain }
  }
  return { ok: true }
}

/**
 * Sort token ids ascending as the contract requires. Ids are opaque integers
 * larger than 2^53, so they are compared as bigint, never as Number.
 */
export function ascending(ids: string[]): string[] {
  return [...new Set(ids)].sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0))
}

/**
 * The custom errors claim() can raise, each as the sentence a person reads.
 * Anything not listed is relayed as "could not claim" plus the raw reason —
 * a sentence we did not write is still better than a hex blob.
 */
export function sentenceFor(errorName: string | undefined, t: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (errorName) {
    case 'NotAllLicences':
      return t('feeVault.errors.notAllLicences')
    case 'NotYourLicence':
      return t('feeVault.errors.notYourLicence')
    case 'NothingToClaim':
    case 'NothingToClaimWith':
      return t('feeVault.errors.nothingToClaim')
    case 'TokenIdsNotAscending':
      return t('feeVault.errors.notAscending')
    case 'EnforcedPause':
      return t('feeVault.errors.paused')
    default:
      return t('feeVault.errors.unknown')
  }
}

/** Pull a custom error's name out of an ethers v5 call exception, if it carries one. */
export function errorNameOf(error: unknown): string | undefined {
  const e = error as { errorName?: string; reason?: string; error?: { errorName?: string }; data?: { errorName?: string } }
  return e?.errorName ?? e?.error?.errorName ?? e?.data?.errorName ?? undefined
}
